// src/components/admin/student/studentFirestore.js
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDoc,
  setDoc,
  query,
  orderBy,
  where,
  Timestamp,
  serverTimestamp,
  writeBatch,
  deleteField,
} from "firebase/firestore";
import { db } from '../../../firebaseConfig';
import { calculateBusinessRoundedHoursFromMinutes } from "../../../utils/sessionHours";
import {
  STUDENT_INACTIVITY_PAUSE_DAYS,
  applyStudentAutoStatusTransitions,
  formatStudentDateKey,
  normalizeStudentScheduleType,
  normalizeStudentStatus,
  parseStudentLifecycleDate,
} from "../../../utils/studentLifecycle";

const SCHEDULED_SESSIONS_COL = "scheduled_sessions";

/**
 * DB safety:
 * - Students remain in `students` (unchanged).
 * - Optional new collections:
 * - studentFieldDefs   (global custom field definitions)
 * - studentUiConfigs   (layout/order/visibility config)
 */

// Roles whose accounts are staff, not students. A student record linked to one
// of these accounts is leftover scaffolding, not a real student.
const STAFF_ROLES = ["tutor", "admin"];

/**
 * Collects the links that tie a `students` record to a STAFF user account.
 *
 * Why this exists: tutor candidates are onboarded as students first so they can
 * sit the SAT diagnostic, which creates a `students` record alongside their
 * `users` record (see submitPublicSignup in functions/index.js). Promoting them
 * to tutor in Account Management only changes `users/{uid}.role` — the student
 * record stays behind and would otherwise keep showing up in the Student
 * Directory. Hiding it here is deliberate rather than deleting the record: the
 * diagnostic they took is linked to it and stays intact and reportable.
 *
 * Both link directions are collected because the two records point at each
 * other: `users/{uid}.studentId` → student doc id, and
 * `students/{id}.diagnosticUid` → user uid.
 */
export async function fetchStaffLinkedStudentRefs() {
  const studentDocIds = new Set();
  const uids = new Set();

  try {
    const snap = await getDocs(collection(db, "users"));
    snap.forEach((d) => {
      const data = d.data() || {};
      const role = String(data.role || "").trim().toLowerCase();
      if (!STAFF_ROLES.includes(role)) return;
      uids.add(d.id);
      const linkedStudentId = String(data.studentId || "").trim();
      if (linkedStudentId) studentDocIds.add(linkedStudentId);
    });
  } catch (e) {
    // Fail open: a permissions or network hiccup must never empty the directory.
    console.error("Could not check which students belong to staff accounts; showing all.", e);
  }

  return { studentDocIds, uids };
}

/** True when this student record is the leftover record of a staff account. */
export function isStaffLinkedStudent(student, staffRefs) {
  if (!student || !staffRefs) return false;
  const studentId = String(student.id || "").trim();
  if (studentId && staffRefs.studentDocIds.has(studentId)) return true;
  const diagnosticUid = String(student.diagnosticUid || "").trim();
  if (diagnosticUid && staffRefs.uids.has(diagnosticUid)) return true;
  return false;
}

export async function fetchStudents() {
  const q = query(collection(db, "students"), orderBy("name", "asc"));
  const [snap, staffRefs] = await Promise.all([getDocs(q), fetchStaffLinkedStudentRefs()]);
  // IMPORTANT:
  // Some student docs contain their own `id` field (often blank from external intake).
  // Always force the Firestore doc id to win.
  return snap.docs
    .map((d) => ({ ...d.data(), id: d.id }))
    .filter((student) => !isStaffLinkedStudent(student, staffRefs));
}

function asMillis(value) {
  if (!value) return 0;
  if (value instanceof Date) return value.getTime();
  if (typeof value?.toDate === "function") return value.toDate().getTime();
  if (typeof value?.seconds === "number") return value.seconds * 1000;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
}

function round2(value) {
  return Math.round((Number(value) + Number.EPSILON) * 100) / 100;
}

function toConferenceDate(entry) {
  const dateMs = asMillis(entry?.date);
  if (dateMs) return new Date(dateMs);
  const createdMs = asMillis(entry?.createdAtMs);
  if (createdMs) return new Date(createdMs);
  return null;
}

function getLatestConferenceDate(student) {
  const rows = Array.isArray(student?.conferences) ? student.conferences : [];
  let latest = null;
  for (let i = 0; i < rows.length; i += 1) {
    const d = toConferenceDate(rows[i]);
    if (!d) continue;
    if (!latest || d.getTime() > latest.getTime()) latest = d;
  }
  return latest;
}

function hasSessions(student, lastSessionDateKey = "") {
  if (lastSessionDateKey) return true;
  const hours = Number(student?.totalSessionHours || 0);
  return Number.isFinite(hours) && hours > 0;
}

function sessionLogHours(logData) {
  const startMs = asMillis(logData?.sessionStart || logData?.start);
  const endMs = asMillis(logData?.sessionEnd || logData?.end);
  if (startMs && endMs && endMs > startMs) {
    const minutes = Math.ceil((endMs - startMs) / 60000);
    return round2(calculateBusinessRoundedHoursFromMinutes(minutes));
  }

  const used = Number(logData?.usedHours);
  if (Number.isFinite(used) && used > 0) {
    const minutes = Math.ceil(used * 60);
    return round2(calculateBusinessRoundedHoursFromMinutes(minutes));
  }
  return 0;
}

function isCanceledSessionStatus(statusValue) {
  const normalized = String(statusValue || "").trim().toLowerCase();
  return normalized.includes("cancel");
}

function startOfDay(dateInput = new Date()) {
  const d = new Date(dateInput);
  d.setHours(0, 0, 0, 0);
  return d;
}

function toLocalDayMs(value) {
  const ms = asMillis(value);
  if (!ms) return 0;
  return startOfDay(new Date(ms)).getTime();
}

async function fetchScheduledSessionDateMeta({ now = new Date() } = {}) {
  const nowMs = now.getTime();
  const todayStartMs = startOfDay(now).getTime();
  const snap = await getDocs(collection(db, SCHEDULED_SESSIONS_COL));
  const latestPastByStudentId = {};
  const nextUpcomingByStudentId = {};
  const upcomingStudentIds = new Set();

  snap.forEach((sessionDoc) => {
    const row = sessionDoc.data() || {};
    const studentId = String(row?.studentId || "").trim();
    if (!studentId) return;
    if (row?.isDeleted) return;
    if (isCanceledSessionStatus(row?.status)) return;

    const startMs = asMillis(row?.start);
    const endMs = asMillis(row?.end);
    const startDayMs = toLocalDayMs(row?.start);
    const endDayMs = toLocalDayMs(row?.end);
    const sessionMomentMs = endMs || startMs || startDayMs || endDayMs;
    if (!sessionMomentMs) return;

    const sessionDayMs = Math.max(startDayMs || 0, endDayMs || 0);

    const isUpcoming = startMs ? startMs >= todayStartMs : sessionDayMs >= todayStartMs;
    if (isUpcoming) {
      upcomingStudentIds.add(studentId);
      const nextMs = Number(nextUpcomingByStudentId[studentId] || 0);
      const candidateUpcoming = startDayMs || endDayMs;
      if (candidateUpcoming && (!nextMs || candidateUpcoming < nextMs)) {
        nextUpcomingByStudentId[studentId] = candidateUpcoming;
      }
    }

    const isPast = endMs ? endMs <= nowMs : (startMs ? startMs <= nowMs : sessionDayMs < todayStartMs);
    if (isPast) {
      const lastMs = Number(latestPastByStudentId[studentId] || 0);
      const candidatePast = sessionDayMs || sessionMomentMs;
      if (candidatePast && (!lastMs || candidatePast > lastMs)) {
        latestPastByStudentId[studentId] = candidatePast;
      }
    }
  });

  return {
    latestPastByStudentId,
    nextUpcomingByStudentId,
    upcomingStudentIds,
  };
}

function pickLatestDateKey(candidates = []) {
  let bestMs = 0;
  let bestKey = "";
  for (let i = 0; i < candidates.length; i += 1) {
    const key = String(candidates[i] || "").trim();
    if (!key) continue;
    const ms = parseStudentLifecycleDate(key)?.getTime() || 0;
    if (ms > bestMs) {
      bestMs = ms;
      bestKey = formatStudentDateKey(new Date(ms));
    }
  }
  return bestKey;
}

async function fetchUpcomingSessionMeta({ now = new Date() } = {}) {
  const fromTs = Timestamp.fromDate(startOfDay(now));
  const snap = await getDocs(
    query(
      collection(db, SCHEDULED_SESSIONS_COL),
      where("start", ">=", fromTs)
    )
  );

  const ids = new Set();
  const nextUpcomingByStudentId = {};
  snap.forEach((sessionDoc) => {
    const row = sessionDoc.data() || {};
    const studentId = String(row?.studentId || "").trim();
    if (!studentId) return;
    if (row?.isDeleted) return;
    if (isCanceledSessionStatus(row?.status)) return;
    const startDayMs = toLocalDayMs(row?.start);
    const endDayMs = toLocalDayMs(row?.end);
    const candidateMs = startDayMs || endDayMs;
    if (!candidateMs) return;
    ids.add(studentId);
    const current = Number(nextUpcomingByStudentId[studentId] || 0);
    if (!current || candidateMs < current) nextUpcomingByStudentId[studentId] = candidateMs;
  });

  return { ids, nextUpcomingByStudentId };
}

function getHoursAtConferenceDate(timeline, conferenceDate) {
  if (!Array.isArray(timeline) || !timeline.length || !conferenceDate) return 0;
  const conferenceEnd = new Date(conferenceDate);
  conferenceEnd.setHours(23, 59, 59, 999);
  const cutoff = conferenceEnd.getTime();

  let cumulative = 0;
  for (let i = 0; i < timeline.length; i += 1) {
    const row = timeline[i];
    if ((Number(row?.endMs) || 0) <= cutoff) cumulative = Number(row?.cumulativeHours) || cumulative;
    else break;
  }
  return round2(cumulative);
}

/**
 * Heavy one-time backfill:
 * - aggregates totalSessionHours from session_logs
 * - re-normalizes stored session_logs.usedHours so future incremental updates stay correct
 * - writes totals to students
 * - also stores baseline hours at latest conference date to support 25h conference-cycle logic
 */
export async function backfillStudentTotalHoursFromSessionLogs(studentsInput = null) {
  const students = Array.isArray(studentsInput) ? studentsInput : await fetchStudents();
  const logsSnap = await getDocs(collection(db, "session_logs"));
  const sessionsByStudent = {};
  const sessionLogUpdates = [];

  logsSnap.forEach((logDoc) => {
    const data = logDoc.data() || {};
    const studentId = String(data?.studentId || "").trim();
    const hours = sessionLogHours(data);
    const currentUsed = Number(data?.usedHours);

    if (hours > 0 && (!Number.isFinite(currentUsed) || Math.abs(currentUsed - hours) >= 0.001)) {
      sessionLogUpdates.push({ id: logDoc.id, usedHours: hours });
    }

    if (!studentId || !hours) return;

    const endMs = asMillis(data?.sessionEnd || data?.end || data?.sessionStart || data?.start || data?.updatedAt);
    if (!sessionsByStudent[studentId]) sessionsByStudent[studentId] = [];
    sessionsByStudent[studentId].push({ endMs, hours });
  });

  const totals = {};
  Object.entries(sessionsByStudent).forEach(([studentId, rows]) => {
    const sorted = [...rows].sort((a, b) => (Number(a?.endMs) || 0) - (Number(b?.endMs) || 0));
    let cumulative = 0;
    const timeline = sorted.map((row) => {
      cumulative += Number(row?.hours) || 0;
      return { endMs: Number(row?.endMs) || 0, cumulativeHours: round2(cumulative) };
    });
    totals[studentId] = {
      totalHours: round2(cumulative),
      timeline,
    };
  });

  const updates = [];
  students.forEach((student) => {
    const studentId = String(student?.id || "").trim();
    if (!studentId) return;

    const totalSessionHours = Number(totals?.[studentId]?.totalHours || 0);
    const latestConferenceDate = getLatestConferenceDate(student);
    const conferenceLastCompletedHours = latestConferenceDate
      ? getHoursAtConferenceDate(totals?.[studentId]?.timeline || [], latestConferenceDate)
      : 0;

    const currentTotal = Number(student?.totalSessionHours || 0);
    const currentConferenceHours = Number(student?.conferenceLastCompletedHours || 0);
    const currentConferenceAt = asMillis(student?.conferenceLastCompletedAt);
    const nextConferenceAt = latestConferenceDate ? latestConferenceDate.getTime() : 0;

    const isSameTotal = Math.abs(currentTotal - totalSessionHours) < 0.001;
    const isSameConfHours = Math.abs(currentConferenceHours - conferenceLastCompletedHours) < 0.001;
    const isSameConfDate = currentConferenceAt === nextConferenceAt;

    if (isSameTotal && isSameConfHours && isSameConfDate) return;

    const patch = {
      totalSessionHours,
      totalSessionHoursUpdatedAt: serverTimestamp(),
      conferenceLastCompletedHours,
      conferenceLastCompletedAt: latestConferenceDate ? latestConferenceDate.toISOString() : "",
      updatedAt: serverTimestamp(),
    };

    updates.push({ studentId, patch });
  });

  if (!updates.length && !sessionLogUpdates.length) return { updatedStudents: 0, updatedSessionLogs: 0 };

  let batch = writeBatch(db);
  let count = 0;
  for (let i = 0; i < updates.length; i += 1) {
    const item = updates[i];
    batch.update(doc(db, "students", item.studentId), item.patch);
    count += 1;
    if (count >= 450) {
      await batch.commit();
      batch = writeBatch(db);
      count = 0;
    }
  }
  if (count > 0) await batch.commit();

  batch = writeBatch(db);
  count = 0;
  for (let i = 0; i < sessionLogUpdates.length; i += 1) {
    const item = sessionLogUpdates[i];
    batch.update(doc(db, "session_logs", item.id), {
      usedHours: item.usedHours,
    });
    count += 1;
    if (count >= 450) {
      await batch.commit();
      batch = writeBatch(db);
      count = 0;
    }
  }
  if (count > 0) await batch.commit();

  return {
    updatedStudents: updates.length,
    updatedSessionLogs: sessionLogUpdates.length,
  };
}

export async function backfillStudentLifecycleFromSessionLogs(
  studentsInput = null,
  {
    inactivityDays = STUDENT_INACTIVITY_PAUSE_DAYS,
    now = new Date(),
  } = {}
) {
  const students = Array.isArray(studentsInput) ? studentsInput : await fetchStudents();
  const logsSnap = await getDocs(collection(db, "session_logs"));
  const scheduledMeta = await fetchScheduledSessionDateMeta({ now });
  const upcomingScheduledStudentIds = scheduledMeta.upcomingStudentIds;
  const lastSessionByStudentId = {};

  logsSnap.forEach((logDoc) => {
    const data = logDoc.data() || {};
    const studentId = String(data?.studentId || "").trim();
    if (!studentId) return;

    const sessionDateMs = parseStudentLifecycleDate(data?.sessionDateKey)?.getTime() || 0;
    const endDayMs = toLocalDayMs(data?.sessionEnd || data?.end);
    const startDayMs = toLocalDayMs(data?.sessionStart || data?.start);
    const candidateMs = Math.max(sessionDateMs || 0, endDayMs || 0, startDayMs || 0);
    if (!candidateMs) return;
    const current = Number(lastSessionByStudentId[studentId] || 0);
    if (candidateMs > current) lastSessionByStudentId[studentId] = candidateMs;
  });

  const updates = [];
  students.forEach((student) => {
    const studentId = String(student?.id || "").trim();
    if (!studentId) return;

    const status = normalizeStudentStatus(student?.status);
    const scheduleType = normalizeStudentScheduleType(student?.scheduleType, student?.status);
    const lastFromLogs = formatStudentDateKey(lastSessionByStudentId[studentId] ? new Date(lastSessionByStudentId[studentId]) : null);
    const lastFromScheduled = formatStudentDateKey(
      scheduledMeta.latestPastByStudentId[studentId]
        ? new Date(scheduledMeta.latestPastByStudentId[studentId])
        : null
    );
    const currentLastSession = formatStudentDateKey(student?.endDate);
    const lastSessionDateKey = pickLatestDateKey([lastFromLogs, lastFromScheduled, currentLastSession]);
    const nextSessionDateKey = formatStudentDateKey(
      scheduledMeta.nextUpcomingByStudentId[studentId]
        ? new Date(scheduledMeta.nextUpcomingByStudentId[studentId])
        : null
    );
    const nextStatus = applyStudentAutoStatusTransitions({
      currentStatus: status,
      hasAnySession: hasSessions(student, lastSessionDateKey),
      hasUpcomingSession: upcomingScheduledStudentIds.has(studentId),
      lastSessionDate: lastSessionDateKey,
      inactivityDays,
      now,
    });

    const patch = {};
    if (student?.status !== nextStatus) patch.status = nextStatus;
    if (student?.scheduleType !== scheduleType) patch.scheduleType = scheduleType;
    if (student?.endDate !== lastSessionDateKey) patch.endDate = lastSessionDateKey;
    if (student?.nextSessionDate !== nextSessionDateKey) patch.nextSessionDate = nextSessionDateKey;

    if (!Object.keys(patch).length) return;
    patch.updatedAt = serverTimestamp();
    updates.push({ studentId, patch });
  });

  if (!updates.length) return { updatedStudents: 0 };

  let batch = writeBatch(db);
  let count = 0;
  for (let i = 0; i < updates.length; i += 1) {
    const item = updates[i];
    batch.update(doc(db, "students", item.studentId), item.patch);
    count += 1;
    if (count >= 450) {
      await batch.commit();
      batch = writeBatch(db);
      count = 0;
    }
  }
  if (count > 0) await batch.commit();

  return { updatedStudents: updates.length };
}

export async function applyStudentStatusAutoTransitions(
  studentsInput = null,
  {
    inactivityDays = STUDENT_INACTIVITY_PAUSE_DAYS,
    now = new Date(),
  } = {}
) {
  const students = Array.isArray(studentsInput) ? studentsInput : await fetchStudents();
  const upcomingMeta = await fetchUpcomingSessionMeta({ now });
  const upcomingScheduledStudentIds = upcomingMeta.ids;
  const updates = [];

  students.forEach((student) => {
    const studentId = String(student?.id || "").trim();
    if (!studentId) return;

    const normalizedStatus = normalizeStudentStatus(student?.status);
    const scheduleType = normalizeStudentScheduleType(student?.scheduleType, student?.status);
    const lastSessionDate = formatStudentDateKey(student?.endDate);
    const nextSessionDate = formatStudentDateKey(
      upcomingMeta.nextUpcomingByStudentId[studentId]
        ? new Date(upcomingMeta.nextUpcomingByStudentId[studentId])
        : null
    );
    const nextStatus = applyStudentAutoStatusTransitions({
      currentStatus: normalizedStatus,
      hasAnySession: hasSessions(student, lastSessionDate),
      hasUpcomingSession: upcomingScheduledStudentIds.has(studentId),
      lastSessionDate,
      inactivityDays,
      now,
    });

    const patch = {};
    if (student?.status !== nextStatus) patch.status = nextStatus;
    if (student?.scheduleType !== scheduleType) patch.scheduleType = scheduleType;
    if (student?.nextSessionDate !== nextSessionDate) patch.nextSessionDate = nextSessionDate;
    if (student?.endDate !== lastSessionDate) patch.endDate = lastSessionDate;
    if (!Object.keys(patch).length) return;

    patch.updatedAt = serverTimestamp();
    updates.push({ studentId, patch });
  });

  if (!updates.length) return { updatedStudents: 0 };

  let batch = writeBatch(db);
  let count = 0;
  for (let i = 0; i < updates.length; i += 1) {
    const item = updates[i];
    batch.update(doc(db, "students", item.studentId), item.patch);
    count += 1;
    if (count >= 450) {
      await batch.commit();
      batch = writeBatch(db);
      count = 0;
    }
  }
  if (count > 0) await batch.commit();

  return { updatedStudents: updates.length };
}

export async function createStudent({ name, grade, startDate = "" }) {
  const payload = {
    name: name.trim(),
    grade: Number(grade),
    status: "Signed Up",
    createdAt: serverTimestamp(),

    // keep existing keys safe
    school: "",
    email: "",
    phone: "",
    dob: "",
    startDate: String(startDate || "").trim(),
    endDate: "",

    parentName: "",
    parentEmail: "",
    parentPhone: "",
    homeAddress: "",

    subjects: [],
    schoolLogin: "",
    loginNote: "",
    scheduleType: "",
    scheduleDate: "",
    scheduleTime: "",
    nextSessionDate: "",

    schoolGrades: [],
    testScores: [],
    totalSessionHours: 0,
    conferenceLastCompletedAt: "",
    conferenceLastCompletedHours: 0,

    customFields: {},
  };
  const ref = await addDoc(collection(db, "students"), payload);
  return { id: ref.id, ...payload };
}

export async function saveStudent(studentId, data) {
  await updateDoc(doc(db, "students", studentId), {
    ...data,
    updatedAt: serverTimestamp(),
  });
}

export async function hardDeleteStudent(studentId) {
  await deleteDoc(doc(db, "students", studentId));
}

/**
 * Bulk-update student grades in batched writes.
 * updates: [{ id, grade }] — grade is the NEW grade value (already computed by the caller).
 * Used by the Student Directory settings (e.g. start-of-year grade advancement).
 */
export async function bulkUpdateStudentGrades(updates) {
  const valid = (Array.isArray(updates) ? updates : []).filter(
    (u) => u && String(u.id || "").trim() && Number.isFinite(Number(u.grade))
  );
  if (!valid.length) return { updated: 0 };

  let batch = writeBatch(db);
  let count = 0;
  for (let i = 0; i < valid.length; i += 1) {
    const item = valid[i];
    batch.update(doc(db, "students", String(item.id)), {
      grade: Number(item.grade),
      updatedAt: serverTimestamp(),
    });
    count += 1;
    if (count >= 450) {
      await batch.commit();
      batch = writeBatch(db);
      count = 0;
    }
  }
  if (count > 0) await batch.commit();

  return { updated: valid.length };
}

export async function fetchFieldDefs() {
  const q = query(collection(db, "studentFieldDefs"), orderBy("createdAt", "asc"));
  const snap = await getDocs(q);
  // Same safety: ensure doc id wins even if the document stores its own `id` field.
  return snap.docs.map((d) => ({ ...d.data(), id: d.id }));
}

export async function addFieldDef(def) {
  // def: {label,type,showInAllStudents,defaultValue,options?}
  const ref = await addDoc(collection(db, "studentFieldDefs"), {
    ...def,
    options: Array.isArray(def.options) ? def.options : [],
    createdAt: serverTimestamp(),
  });
  return ref.id;
}

// Deletes the field definition AND removes the stored values from all students.
export async function deleteStudentFieldDefAndData(fieldDefId) {
  // 1) Delete the definition
  await deleteDoc(doc(db, "studentFieldDefs", fieldDefId));

  // 2) Remove this field from all students (customFields.<id>)
  const snap = await getDocs(collection(db, "students"));

  let batch = writeBatch(db);
  let count = 0;

  async function commitBatch() {
    await batch.commit();
    batch = writeBatch(db);
    count = 0;
  }

  for (const d of snap.docs) {
    batch.update(doc(db, "students", d.id), {
      [`customFields.${fieldDefId}`]: deleteField(),
    });
    count++;
    if (count >= 450) await commitBatch(); // keep under 500 writes
  }

  if (count > 0) await commitBatch();
}

/**
 * UI layout config
 * Stored in: studentUiConfigs/studentFormLayout
 * { layout: [{key,label,visible}], updatedAt }
 */
export async function fetchLayoutConfig(defaultLayout) {
  const ref = doc(db, "studentUiConfigs", "studentFormLayout");
  const snap = await getDoc(ref);
  if (snap.exists()) return snap.data().layout || defaultLayout;

  // Create on first run
  await setDoc(ref, { layout: defaultLayout, updatedAt: serverTimestamp() });
  return defaultLayout;
}

export async function saveLayoutConfig(layout) {
  const ref = doc(db, "studentUiConfigs", "studentFormLayout");
  await setDoc(ref, { layout, updatedAt: serverTimestamp() }, { merge: true });
}
